import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { CITIES } from '@/constants';
import { MapPin, Users, CheckCircle, Clock } from 'lucide-react';

export default function Houses() {
  return (
    <div className="bg-white" data-testid="houses-page">
      <section className="py-24 bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-5xl sm:text-6xl font-unbounded font-black mb-8"
            data-testid="houses-title"
          >
            Our Houses
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-xl font-manrope max-w-4xl"
          >
            From LA to nationwide. Each house is a hub for builders to live, learn, and ship.
          </motion.p>
        </div>
      </section>

      <section className="py-24" data-testid="cities-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {CITIES.map((city, index) => (
              <motion.div
                key={city.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white border-2 border-black/10 hover:border-accent transition-colors overflow-hidden group"
                data-testid={`city-card-${city.id}`}
              >
                <div
                  className="h-64 bg-cover bg-center grayscale group-hover:grayscale-0 transition-all"
                  style={{ backgroundImage: `url('${city.image}')` }}
                />
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-2xl font-unbounded font-bold">{city.name}</h3>
                    {city.status === 'live' ? (
                      <span className="bg-accent text-white px-3 py-1 text-sm font-manrope font-medium" data-testid="live-badge">
                        LIVE
                      </span>
                    ) : (
                      <span className="bg-gray-200 text-gray-700 px-3 py-1 text-sm font-manrope font-medium" data-testid="coming-soon-badge">
                        COMING SOON
                      </span>
                    )}
                  </div>
                  <div className="space-y-2 text-gray-700 font-manrope">
                    <div className="flex items-center gap-2">
                      <Users size={16} />
                      <span>Capacity: {city.capacity} residents</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <MapPin size={16} />
                      <span>{city.name}</span>
                    </div>
                  </div>
                  {city.status === 'live' && (
                    <Link
                      to="/apply"
                      className="mt-6 block bg-black text-white px-6 py-3 font-bold uppercase tracking-wide text-center hover:bg-accent transition-colors"
                      data-testid="apply-link"
                    >
                      Apply Now
                    </Link>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-gray-50" data-testid="la-details-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <h2 className="text-4xl font-unbounded font-black mb-8">Los Angeles House</h2>
              <p className="text-lg font-manrope text-gray-700 mb-6">
                Our flagship location in the heart of LA's tech scene. Fully furnished, walking distance to coffee shops, coworking spaces, and the builder community.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="text-accent flex-shrink-0 mt-1" size={20} />
                  <p className="font-manrope"><strong>Capacity:</strong> 8 residents</p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="text-accent flex-shrink-0 mt-1" size={20} />
                  <p className="font-manrope"><strong>Amenities:</strong> High-speed internet, workspace, kitchen, lounge</p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="text-accent flex-shrink-0 mt-1" size={20} />
                  <p className="font-manrope"><strong>Next Cohort:</strong> Q1 2025</p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="text-accent flex-shrink-0 mt-1" size={20} />
                  <p className="font-manrope"><strong>Eligibility:</strong> Open to all skill levels</p>
                </div>
              </div>
            </div>
            <div
              className="h-96 bg-cover bg-center"
              style={{ backgroundImage: "url('https://images.unsplash.com/photo-1758548157747-285c7012db5b')" }}
            />
          </div>
        </div>
      </section>

      <section className="py-24" data-testid="local-partners-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-unbounded font-black mb-12">Local Partners</h2>
          <p className="text-lg font-manrope text-gray-700 mb-8">
            We work with local tech companies, investors, and community leaders to provide mentorship, networking, and demo day opportunities.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-gray-100 aspect-square flex items-center justify-center border border-black/10" data-testid={`partner-placeholder-${i}`}>
                <span className="font-manrope text-gray-400">Partner Logo</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
